import { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom';

import {
  LOG_PREFIX,
  logInfo,
  type AuthContext,
  detectDomainType,
  resolveDomainConfig,
  parseRouteParams,
  checkEnvironment,
  isPopupMode,
  runDomainFlow,
} from './core';

import Loading from '@/components/Loading';
import { getUserInfoLocal } from '@/utils/storage';
import SnapshotMask from '@/components/SnapshotMask';

/**
 * 微信授权路由守卫
 */
const WxAuthGuard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [isSnapshot, setIsSnapshot] = useState(() => Boolean(getUserInfoLocal()?.isSnapShotUser));
  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const init = async () => {
      // 解析 URL 参数
      const urlParams = parseRouteParams(searchParams);

      // 1. 弹窗模式：跳过授权
      if (isPopupMode(urlParams)) {
        logInfo(`${LOG_PREFIX} 弹窗模式，跳过授权`);
        setLoading(false);
        return;
      }

      // 2. 环境检查
      const envError = checkEnvironment(urlParams.platform, urlParams.skipWxAuth);
      if (envError) {
        navigate(envError, { replace: true });
        return;
      }

      // 3. 获取域名配置
      const domains = await resolveDomainConfig(urlParams, navigate);
      if (!domains) return;

      // 4. 检测域名类型
      const domainType = detectDomainType(domains);
      logInfo(`${LOG_PREFIX} 域名类型: ${domainType}`);

      // 构建授权上下文
      const context: AuthContext = { ...urlParams, domains, searchParams };

      // 5. 根据域名类型处理
      await runDomainFlow(domainType, context, setLoading, navigate);
      setIsSnapshot(Boolean(getUserInfoLocal()?.isSnapShotUser));
    };

    init();
  }, [navigate, searchParams]);

  if (loading) {
    return <Loading text='正在授权...' />;
  }

  return (
    <>
      <Outlet />
      <SnapshotMask visible={isSnapshot} />
    </>
  );
};

export default WxAuthGuard;
